/**
 * GIWAHS Adaptive Journey Core Engine — JourneyContext (AJIP-16.02.003 · WS7)
 * ---------------------------------------------------------------------
 * Framework-agnostic single source of truth for the active journey.
 *
 * Per Founder Ruling 01 — this module does not depend on React,
 * Redux, Zustand, or any UI framework. It is pure JavaScript. Any
 * future React consumer will build a thin adapter on top of this class;
 * that adapter is out of scope for this package.
 *
 * The Journey Context stores, at minimum:
 *   activeJourney      the frozen journey definition
 *   currentState       one of ALL_STATES
 *   currentStage       optional opaque stage identifier
 *   progress           float in [0, 1] (0 by default)
 *   metadata           frozen object; opaque to the engine
 *   flagStatus         frozen snapshot of the master flag state at
 *                      construction time (immutable for the lifetime
 *                      of the context)
 *   configuration      alias to the frozen definition
 *
 * Contexts are constructed by the Engine and never by consumers.
 * Consumers subscribe to changes via subscribe(handler).
 */
import { STATES, isValidState } from './JourneyState';
import { InvalidRequestError } from './JourneyErrors';

class JourneyContext {
  constructor({ definition, flagStatus }) {
    if (!definition) throw new InvalidRequestError('JourneyContext requires a definition');
    this._activeJourney = definition;
    this._currentState = STATES.NOT_STARTED;
    this._currentStage = null;
    this._progress = 0;
    this._metadata = Object.freeze({});
    this._flagStatus = Object.freeze({ ...flagStatus });
    this._subscribers = new Set();
  }

  get activeJourney() { return this._activeJourney; }
  get configuration() { return this._activeJourney; }
  get currentState() { return this._currentState; }
  get currentStage() { return this._currentStage; }
  get progress() { return this._progress; }
  get metadata() { return this._metadata; }
  get flagStatus() { return this._flagStatus; }

  snapshot() {
    return Object.freeze({
      identifier: this._activeJourney.identifier,
      currentState: this._currentState,
      currentStage: this._currentStage,
      progress: this._progress,
      metadata: this._metadata,
      flagStatus: this._flagStatus,
    });
  }

  subscribe(handler) {
    if (typeof handler !== 'function') return () => {};
    this._subscribers.add(handler);
    return () => this._subscribers.delete(handler);
  }

  _notify(kind) {
    const snap = this.snapshot();
    for (const h of this._subscribers) {
      try { h({ kind, snapshot: snap }); } catch (_e) { /* isolate handler faults */ }
    }
  }

  _setState(nextState) {
    if (!isValidState(nextState)) {
      throw new InvalidRequestError(`Invalid state '${nextState}'`);
    }
    if (nextState === this._currentState) return false;
    this._currentState = nextState;
    this._notify('state');
    return true;
  }

  _setStage(nextStage) {
    if (nextStage !== null && typeof nextStage !== 'string') {
      throw new InvalidRequestError('stage must be string or null');
    }
    if (nextStage === this._currentStage) return false;
    this._currentStage = nextStage;
    this._notify('stage');
    return true;
  }

  _setProgress(next) {
    if (typeof next !== 'number' || Number.isNaN(next) || next < 0 || next > 1) {
      throw new InvalidRequestError('progress must be a number in [0, 1]');
    }
    if (next === this._progress) return false;
    this._progress = next;
    this._notify('progress');
    return true;
  }

  _mergeMetadata(patch) {
    if (patch === null || patch === undefined) return false;
    if (typeof patch !== 'object' || Array.isArray(patch)) {
      throw new InvalidRequestError('metadata patch must be a plain object');
    }
    this._metadata = Object.freeze({ ...this._metadata, ...patch });
    this._notify('metadata');
    return true;
  }
}

export { JourneyContext };
